import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DepositService } from '@lx/quote-tool/shared/services';
import { QuoteLeadOrigin, QuoteLeadOriginEnum, QuoteLeadOutputV5, QuotePayableData } from '@lx/quote-tool/shared/types';
import { LoggerService } from '@lx/shared/logger';
import { FlywireGatewayV2Service, PAYMENT_GATEWAY_V2_SERVICE } from '@lx/shared/payment';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { PricePipe } from '@lx/shared/ui/pipes';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { combineLatest, EMPTY, first, map, Observable, take, tap } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { ComponentVisibilityService, LeadService, NavigationService, PackageDetailsOrchestratorService } from '../../../services';
import { QuoteStore, QuoteToolLiteState, QuoteToolLiteStore } from '../../../store';
import { PACKAGE_DETAILS_CONTAINER_ID, Step } from '../../../types';
import { EnrolmentFormComponent } from '../../enrolment-form/enrolment-form.component';
import { slideDepositFooterAnimation } from './deposit-footer.animation';
import { HttpErrorResponse } from '@angular/common/http';
import { withErrorType } from '../../../mixins';
import { GoogleAnalyticsErrorService } from '../../../google-analytics';
import { Flows } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  imports: [EnrolmentFormComponent, AsyncPipe, PricePipe, NgIf],
  selector: 'lx-booking-step',
  templateUrl: './booking-step.component.html',
  styleUrls: ['./booking-step.component.scss'],
  animations: [slideDepositFooterAnimation],
  providers: [{ provide: PAYMENT_GATEWAY_V2_SERVICE, useClass: FlywireGatewayV2Service }, DepositService],
})
export class BookingStepComponent implements OnInit {
  payableData$: Observable<QuotePayableData> | undefined;
  currency$: Observable<string> | undefined;

  visibleDepositFooter$: Observable<boolean> | undefined;

  private readonly quoteStore = inject(QuoteStore);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly navigationService = inject(NavigationService);
  private readonly leadService = inject(LeadService);
  private readonly paymentGatewayService = inject(PAYMENT_GATEWAY_V2_SERVICE);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly componentVisibilityService = inject(ComponentVisibilityService);
  private readonly packageDetailsOrchestratorService = inject(PackageDetailsOrchestratorService);
  private readonly loadingService = inject(LoadingService);
  private readonly googleAnalyticsErrorService = inject(GoogleAnalyticsErrorService);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.paymentGatewayService.bootstrapV2();
    this.payableData$ = this.quoteStore.payableData$;
    this.currency$ = this.quoteToolLiteStore.selectedCurrency$;

    this.visibleDepositFooter$ = combineLatest([
      this.breakpointObserverService.handheld$,
      this.componentVisibilityService.getOrCreateVisibility(PACKAGE_DETAILS_CONTAINER_ID),
    ]).pipe(map(([handheld, packageDetailsVisible]: [boolean, boolean]) => handheld && !packageDetailsVisible));
  }

  submitLead() {
    this.loadingService.startLoading($localize`Sending your enrolment...`);
    this.quoteToolLiteStore.selectionForLeads$
      .pipe(
        first(),
        switchMap((state) => this.createLeadAndStep(state)),
        finalize(() => this.loadingService.stopLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async ([quoteLeadOutput, step]) => {
        this.quoteToolLiteStore.updateState({ quoteId: quoteLeadOutput?.quoteId });
        this.loadingService.stopLoading();
        try {
          const result = await this.paymentGatewayService.makePaymentV2(quoteLeadOutput.paymentSessionData.id);
          if (result.success) {
            this.navigationService.navigateToNextStep(step);
          }
        } catch (e) {
          if (e?.errors?.[0]?.type === 'cancellation') {
            return;
          }
          this.trackErrorForStep(step, e);
          this.logger.error('Error during payment:', e);
          this.navigationService.navigateToNextStep(step);
        }
      });
  }

  showQuoteBreakdown() {
    this.packageDetailsOrchestratorService.setQuoteBreakdownVisibility(true);
  }

  private createLeadAndStep(state: QuoteToolLiteState): Observable<[QuoteLeadOutputV5, Step]> {
    const formOrigin = this.getFormOrigin(state);
    const step$ = this.quoteToolLiteStore.currentStep$.pipe(take(1));

    const lead$ = this.createLead(state, formOrigin).pipe(
      // If lead creation fails, track and complete the stream
      catchError((error: HttpErrorResponse | unknown) =>
        step$.pipe(
          tap((step) => this.trackErrorForStep(step, error)),
          switchMap(() => EMPTY),
        ),
      ),
    );

    return combineLatest([lead$, step$]);
  }

  private getFormOrigin(state: QuoteToolLiteState): QuoteLeadOrigin {
    return state.activeFlow === Flows.Online ? QuoteLeadOriginEnum.ONLINE_COURSES_ENROLMENT : QuoteLeadOriginEnum.ENROLMENT;
  }

  private createLead(state: QuoteToolLiteState, formOrigin: QuoteLeadOrigin) {
    return this.leadService.createLead(state, formOrigin).pipe(tap((out: QuoteLeadOutputV5) => this.onLeadCreated(out)));
  }

  private onLeadCreated(out: QuoteLeadOutputV5): void {
    this.quoteToolLiteStore.updateState({ quoteId: out?.quoteId });
  }

  private trackErrorForStep(step: Step, error: unknown): void {
    const { getErrorType, getErrorMessage } = withErrorType();
    const type = getErrorType(error);
    const message = getErrorMessage(error);

    this.googleAnalyticsErrorService.trackFormSubmissionFailure(step?.googleAnalyticsEvent, message, type);
  }
}
